import { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useSuppliers } from '../../hooks/useSuppliers';
import { useSupplierFilterOptions, useSubRegions, useVarieties } from '../../hooks/useReferenceData';

interface BulkWineFiltersProps {
  initialValues?: BulkWineFilterValues;
  onFiltersReady?: (getFilters: () => BulkWineFilterValues) => void;
}

export interface BulkWineFilterValues {
  code: string;
  supplierId: string;
  vintage: string;
  volume: string;
  varietyIds: string[];
  countryId: string;
  regionId: string;
  subRegionId: string;
  locationRegionId: string;
  locationSubRegionId: string;
}

const defaultFilters: BulkWineFilterValues = {
  code: '',
  supplierId: '',
  vintage: '',
  volume: '',
  varietyIds: [],
  countryId: '',
  regionId: '',
  subRegionId: '',
  locationRegionId: '',
  locationSubRegionId: ''
};

interface Region {
  id: number;
  name: string;
  countryId: number;
}

interface SubRegion {
  id: number;
  name: string;
  regionId: number;
}

interface Country {
  id: number;
  name: string;
}

interface Supplier {
  id: number;
  name: string;
}

interface Variety {
  id: number;
  name: string;
}

export const BulkWineFilters = ({ initialValues, onFiltersReady }: BulkWineFiltersProps): JSX.Element => {
  const supplierParams = new URLSearchParams({ page: '1', limit: '1000' });
  const { data: supplierOptions } = useSupplierFilterOptions();
  const { data: suppliersResponse } = useSuppliers(supplierParams);
  const { data: varietiesData } = useVarieties();
  const { data: subRegionsData } = useSubRegions();
  const [filters, setFilters] = useState<BulkWineFilterValues>(initialValues || defaultFilters);
  const regions = (supplierOptions?.regions || []) as Region[];
  const countries = (supplierOptions?.countries || []) as Country[];
  const suppliers = (suppliersResponse?.data || []) as Supplier[];
  const varieties = (varietiesData || []) as Variety[];
  const subRegions = (subRegionsData || []) as SubRegion[];
  const [varietyDropdownOpen, setVarietyDropdownOpen] = useState(false);
  const varietyDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (varietyDropdownRef.current && !varietyDropdownRef.current.contains(event.target as Node)) {
        setVarietyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update local state when initialValues change
  useEffect(() => {
    if (initialValues === undefined) {
      // Parent cleared filters - reset to defaults
      setFilters(defaultFilters);
    } else {
      setFilters(initialValues);
    }
  }, [initialValues]);

  // Provide a way for parent to get current filter values
  useEffect(() => {
    if (onFiltersReady) {
      onFiltersReady(() => filters);
    }
  }, [filters, onFiltersReady]);

  const handleChange = (field: keyof BulkWineFilterValues, value: string | string[]) => {
    setFilters(prev => {
      const next = { ...prev, [field]: value };
      // Region/sub-region selections are only valid within their parent.
      if (field === 'countryId') {
        next.regionId = '';
        next.subRegionId = '';
      }
      if (field === 'regionId') {
        next.subRegionId = '';
      }
      return next;
    });
  };

  const handleVarietyToggle = (varietyId: string) => {
    setFilters(prev => {
      const currentIds = prev.varietyIds;
      const newIds = currentIds.includes(varietyId)
        ? currentIds.filter(id => id !== varietyId)
        : [...currentIds, varietyId];
      return { ...prev, varietyIds: newIds };
    });
  };

  // Filter regions based on selected country - show all if no country selected
  const filteredRegions = filters.countryId
    ? regions.filter(r => r.countryId.toString() === filters.countryId)
    : regions;

  const regionOptionGroups = filteredRegions.map(region => ({
    region,
    subRegions: subRegions.filter(sr => String(sr.regionId) === String(region.id)),
  }));

  // Sub-region selections encode as `s:<id>`, plain regions as `r:<id>`.
  const regionSelectValue = filters.subRegionId
    ? `s:${filters.subRegionId}`
    : filters.regionId
      ? `r:${filters.regionId}`
      : '';

  const handleRegionSelectChange = (value: string) => {
    if (!value) {
      setFilters(prev => ({ ...prev, regionId: '', subRegionId: '' }));
      return;
    }

    const [kind, rawId] = value.split(':');
    setFilters(prev =>
      kind === 's'
        ? {
            ...prev,
            regionId: String(subRegions.find(sr => String(sr.id) === rawId)?.regionId ?? prev.regionId),
            subRegionId: rawId,
          }
        : { ...prev, regionId: rawId, subRegionId: '' }
    );
  };

  // Location offers the same options as Region but always over the full region list:
  // where a wine is stored is independent of where its fruit was grown, so the Country
  // filter above must not narrow it.
  const locationOptionGroups = regions.map(region => ({
    region,
    subRegions: subRegions.filter(sr => String(sr.regionId) === String(region.id)),
  }));

  const locationSelectValue = filters.locationSubRegionId
    ? `s:${filters.locationSubRegionId}`
    : filters.locationRegionId
      ? `r:${filters.locationRegionId}`
      : '';

  const handleLocationSelectChange = (value: string) => {
    if (!value) {
      setFilters(prev => ({ ...prev, locationRegionId: '', locationSubRegionId: '' }));
      return;
    }

    const [kind, rawId] = value.split(':');
    setFilters(prev =>
      kind === 's'
        ? {
            ...prev,
            locationRegionId: String(
              subRegions.find(sr => String(sr.id) === rawId)?.regionId ?? prev.locationRegionId
            ),
            locationSubRegionId: rawId,
          }
        : { ...prev, locationRegionId: rawId, locationSubRegionId: '' }
    );
  };

  const inputStyle = {
    width: '100%',
    height: '40px',
    padding: '10px 14px',
    borderRadius: '6px',
    border: '1px solid #D0D5DD',
    fontSize: '14px',
    fontFamily: 'Oxygen',
    color: '#101828',
    backgroundColor: '#FFFFFF',
    outline: 'none'
  };

  const labelStyle = {
    display: 'block',
    fontFamily: 'Oxygen',
    fontSize: '14px',
    fontWeight: 700,
    color: '#344054',
    marginBottom: '6px'
  };

  return (
    <div className="w-full" style={{ paddingTop: '24px' }}>
      <h2 style={{
        fontFamily: 'Oxygen',
        fontSize: '24px',
        fontWeight: 700,
        color: '#101828',
        marginBottom: '32px'
      }}>
        Bulk Wine
      </h2>

      <div className="space-y-6">
        {/* Code */}
        <div>
          <label style={labelStyle}>Code</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="Enter code"
            value={filters.code}
            onChange={(e) => handleChange('code', e.target.value)}
          />
        </div>

        {/* Supplier */}
        <div>
          <label style={labelStyle}>Supplier</label>
          <select
            style={inputStyle}
            value={filters.supplierId}
            onChange={(e) => handleChange('supplierId', e.target.value)}
          >
            <option value="">All Suppliers</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>

        {/* Vintage */}
        <div>
          <label style={labelStyle}>Vintage</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="Enter vintage year"
            value={filters.vintage}
            onChange={(e) => handleChange('vintage', e.target.value)}
          />
        </div>

        {/* Volume */}
        <div>
          <label style={labelStyle}>Volume (L)</label>
          <input
            type="text"
            style={inputStyle}
            placeholder="Enter volume"
            value={filters.volume}
            onChange={(e) => handleChange('volume', e.target.value)}
          />
        </div>

        {/* Variety */}
        <div style={{ position: 'relative' }} ref={varietyDropdownRef}>
          <label style={labelStyle}>Variety</label>
          <div
            onClick={() => setVarietyDropdownOpen(!varietyDropdownOpen)}
            style={{
              ...inputStyle,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span style={{ color: filters.varietyIds.length > 0 ? '#101828' : '#98A2B3' }}>
              {filters.varietyIds.length > 0
                ? `${filters.varietyIds.length} selected`
                : 'Select Variety...'}
            </span>
            <ChevronDown size={20} color="#667085" strokeWidth={1.67} aria-hidden style={{ transform: varietyDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </div>
          
          {varietyDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #D0D5DD',
                borderRadius: '6px',
                maxHeight: '240px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)'
              }}
            >
              {varieties.map(variety => (
                <label
                  key={variety.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontFamily: 'Oxygen',
                    fontSize: '14px',
                    color: '#344054'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <input
                    type="checkbox"
                    checked={filters.varietyIds.includes(variety.id.toString())}
                    onChange={() => handleVarietyToggle(variety.id.toString())}
                    className="checkbox-custom"
                    style={{
                      marginRight: '8px'
                    }}
                  />
                  <span>{variety.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Country */}
        <div>
          <label style={labelStyle}>Country</label>
          <select
            style={inputStyle}
            value={filters.countryId}
            onChange={(e) => handleChange('countryId', e.target.value)}
          >
            <option value="">All Countries</option>
            {countries.map(country => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>

        {/* Region — sub-regions are nested under their parent region in the same
            dropdown, so there is no separate sub-region filter. */}
        <div>
          <label style={labelStyle}>Region</label>
          <select
            style={inputStyle}
            value={regionSelectValue}
            onChange={(e) => handleRegionSelectChange(e.target.value)}
          >
            <option value="">All Regions</option>
            {regionOptionGroups.map(({ region, subRegions: regionSubRegions }) =>
              regionSubRegions.length > 0 ? (
                <optgroup key={region.id} label={region.name}>
                  <option value={`r:${region.id}`}>{region.name}</option>
                  {regionSubRegions.map(subRegion => (
                    <option key={subRegion.id} value={`s:${subRegion.id}`}>
                      {subRegion.name}
                    </option>
                  ))}
                </optgroup>
              ) : (
                <option key={region.id} value={`r:${region.id}`}>
                  {region.name}
                </option>
              )
            )}
          </select>
        </div>

        {/* Location — where the wine is stored, for transport cost estimates. Same
            options as Region; sub-regions nested under their parent. */}
        <div>
          <label style={labelStyle}>Location</label>
          <select
            style={inputStyle}
            value={locationSelectValue}
            onChange={(e) => handleLocationSelectChange(e.target.value)}
          >
            <option value="">All Locations</option>
            {locationOptionGroups.map(({ region, subRegions: regionSubRegions }) =>
              regionSubRegions.length > 0 ? (
                <optgroup key={region.id} label={region.name}>
                  <option value={`r:${region.id}`}>{region.name}</option>
                  {regionSubRegions.map(subRegion => (
                    <option key={subRegion.id} value={`s:${subRegion.id}`}>
                      {subRegion.name}
                    </option>
                  ))}
                </optgroup>
              ) : (
                <option key={region.id} value={`r:${region.id}`}>
                  {region.name}
                </option>
              )
            )}
          </select>
        </div>
      </div>
    </div>
  );
};
